import axios from 'axios';

export interface Site {
  id: number;
  uuid_id: string;
  name: string;
  description: string | null;
  company_name: string | null;
  is_active: boolean;
  gridbox_diff_multiplier?: number | null;
  api_config: SiteApiConfig | null;
  plant_count: number;
  ekk_device_count?: number;
  meter_count?: number;
}

export type SiteAccessStatus='not-configured'|'disabled'|'no-data'|'online'|'offline'|'partial';
export interface SiteOverviewRow {
  tags?:Array<{id:number;label:string;color:string}>;
  has_osos?:boolean;has_ekk?:boolean;has_power_meter?:boolean;
  uuid_id:string;name:string;description:string|null;company_name:string|null;is_active:boolean;plant_count:number;investor_name:string|null;
  investors?:Array<{uuid_id:string;name:string}>;
  energy_today_kwh:number|null;ekk_status:SiteAccessStatus;ekk_net_kw:number|null;ekk_export_kw:number|null;ekk_last_data_at:number|null;
  smartlogger_status:SiteAccessStatus;smartlogger_count:number;smartlogger_online_count:number;smartlogger_last_data_at:number|null;
  smartlogger_power_kw:number|null;smartlogger_energy_today_kwh:number|null;
}

export async function fetchSiteOverview(signal?:AbortSignal):Promise<{rows:SiteOverviewRow[];fetchedAt:number}> {
  return (await apiClient.get('/overview',{signal,timeout:30000})).data;
}

export interface SiteEkkDevice {
  id:number;
  uuid_id:string;
  name:string;
  serial_number:string;
  brand:string;
  model:string;
  status:number;
  modbus_host:string;
  modbus_port:number;
  modbus_unit_id:number;
  web_base_url:string|null;
  register_count:number;
}

export type SiteEkkInput=Omit<SiteEkkDevice,'id'|'uuid_id'|'register_count'> & {template_device_id?:number|null;template_type_id?:number|null};
export type SiteEkkTemplate={id:number;name?:string;source?:'type';brand:string;model:string;register_count:number};

export async function fetchSiteEkkConfig(uuid:string):Promise<SiteEkkDevice|null> {
  return (await apiClient.get(`/${uuid}/ekk/config`)).data.device;
}

export async function saveSiteEkkConfig(uuid:string,data:SiteEkkInput):Promise<SiteEkkDevice> {
  return (await apiClient.put(`/${uuid}/ekk/config`,data)).data.device;
}

export async function fetchEkkTemplates():Promise<SiteEkkTemplate[]> {
  return (await apiClient.get('/ekk/templates')).data.templates;
}

export interface SiteApiConfig {
  gridbox?: SiteGridboxConfig | null;
}

export interface SiteGridboxConfig {
  url?: string;
  username?: string;
  password?: string;
  wiringNo?: string | null;
}

export interface SitePlant {
  id: number;
  uuid_id: string;
  name: string;
  status: number;
}

export interface PlantForSelection {
  id: number;
  uuid_id: string;
  name: string;
  site_id: number | null;
  site_name: string | null;
}

export interface GridboxSummaryRequest {
  wiringNo?: string;
  endexStartDate?: string;
  endexEndDate?: string;
  isBilling?: boolean;
  loadProfileStartDate?: string;
  loadProfileEndDate?: string;
}

export interface GridboxSummaryResponse {
  ok: boolean;
  summary: {
    siteName: string | null;
    connection: {
      ok: boolean;
      baseUrl: string;
      wiringNo: string | null;
      username: string;
      passwordConfigured: boolean;
    };
    lastEndex: unknown;
    endexes: unknown[];
    lastLoadProfileDate: unknown | null;
    loadProfiles: unknown[] | null;
    fetchedAt: string;
  };
}

export interface GridboxArchiveRow {
  id: number;
  siteId: number;
  ProfileDate: string;
  WiringNo: string | null;
  MeterSerialNumber: string | null;
  ActiveEndex: string | null;
  RC: string | null;
  RI: string | null;
  ActiveEndexOut: string | null;
  RCOut: string | null;
  RIOut: string | null;
  ActiveEndexWithMultiplier: string | null;
  RCWithMultiplier: string | null;
  RIWithMultiplier: string | null;
  ActiveEndexOutWithMultiplier: string | null;
  RCOutWithMultiplier: string | null;
  RIOutWithMultiplier: string | null;
  CalculatedLoadProfileReadType: string | null;
  ActiveEndexDifference: string | null;
  ActiveEndexOutDiff: string | null;
  ActiveT0IndexDifference: string | null;
  ActiveT0IndexExportDifference: string | null;
  MeterTimeDifference: string | null;
  ReactiveIndexQ1Difference: string | null;
  ReactiveIndexQ2Difference: string | null;
  ReactiveIndexQ3Difference: string | null;
  ReactiveIndexQ4Difference: string | null;
  Lat: string | null;
  Long: string | null;
  IsProductionWiring: boolean | null;
  HasLicence: boolean | null;
  PowerSource: string | null;
  createdAt: string;
}

export interface GridboxArchiveResponse {
  ok: boolean;
  siteName: string | null;
  count: number;
  latest: GridboxArchiveRow | null;
  rows: GridboxArchiveRow[];
  fetchedAt: string;
}

export interface GridboxEndexRow {
  id: number;
  siteId: number;
  endexDate: string;
  isBilling: boolean;
  WiringNo: string | null;
  MeterSerialNumber: string | null;
  EndexDate: string;
  MaxDemand: string | null;
  MaxDemandDate: string | null;
  MaxDemandOut: string | null;
  MaxDemandOutDate: string | null;
  RC: string | null;
  RCOut: string | null;
  RI: string | null;
  RIOut: string | null;
  T0: string | null;
  T0Out: string | null;
  T1: string | null;
  T1Out: string | null;
  T2: string | null;
  T2Out: string | null;
  T3: string | null;
  T3Out: string | null;
  T4: string | null;
  T4Out: string | null;
  TToplam: string | null;
  TOutToplam: string | null;
  Multiplier: string | null;
  createdAt: string;
}

export interface GridboxEndexArchiveResponse {
  ok: boolean;
  siteName: string | null;
  isBilling: boolean;
  count: number;
  latest: GridboxEndexRow | null;
  rows: GridboxEndexRow[];
  fetchedAt: string;
}

export interface SiteProductionSeriesRow {
  periodIndex: number;
  label: string;
  startAt: string;
  production: number;
}

export interface SiteProductionSeriesResponse {
  ok: boolean;
  siteName: string | null;
  bucket: 'hour' | 'day' | 'month' | 'year';
  rows: SiteProductionSeriesRow[];
  fetchedAt: string;
}

export interface SiteEkkCurrentRow {
  deviceId: number;
  deviceName: string;
  serialNumber: string | null;
  brand: string | null;
  model: string | null;
  webBaseUrl: string | null;
  readingId: number | null;
  logtime: string | null;
  id: number;
  quantity: string;
  dataType: string | null;
  units: string | null;
  webPageTag: string | null;
  notes: string | null;
  valueNumeric: number | null;
  valueText: string | null;
}

export interface SiteEkkCurrentResponse {
  ok: boolean;
  siteName: string;
  rows: SiteEkkCurrentRow[];
}

export interface SiteEkkHistoryRow {
  timestamp: string;
  valueNumeric: number;
}

export interface SiteEkkHistoryResponse {
  ok: boolean;
  date: string;
  rows: SiteEkkHistoryRow[];
}

export interface SiteProductionSeriesRequest {
  bucket?: 'hour' | 'day' | 'month' | 'year';
  year?: number;
  month?: number;
  day?: number;
}

export interface GridboxTestResponse {
  ok: boolean;
  result: {
    tokenExpiresIn: number;
    checks: {
      token: { ok: boolean; message: string };
      endex: { ok: boolean; message: string };
      billingEndex: { ok: boolean; message: string };
      loadProfile: { ok: boolean; message: string };
    };
  };
}

const apiClient = axios.create({
  baseURL: '/api/sites',
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const fetchSites = async (): Promise<Site[]> => {
  const response = await apiClient.get('/');
  return response.data;
};

export const createSite = async (data: Partial<Site>): Promise<Site> => {
  const response = await apiClient.post('/', data);
  return response.data;
};

export const updateSite = async (uuidId: string, data: Partial<Site>): Promise<Site> => {
  const response = await apiClient.put(`/${uuidId}`, data);
  return response.data;
};

export const deleteSite = async (uuidId: string): Promise<void> => {
  await apiClient.delete(`/${uuidId}`);
};

export const fetchSitePlants = async (uuidId: string): Promise<SitePlant[]> => {
  const response = await apiClient.get(`/${uuidId}/plants`);
  return response.data;
};

export const fetchAllPlantsForSelection = async (): Promise<PlantForSelection[]> => {
  const response = await apiClient.get('/plants/all');
  return response.data;
};

export const assignPlantsToSite = async (uuidId: string, plantUuids: string[]): Promise<void> => {
  await apiClient.post(`/${uuidId}/plants`, { plant_uuids: plantUuids });
};

export const removePlantFromSite = async (plantUuid: string): Promise<void> => {
  await apiClient.delete(`/plants/${plantUuid}`);
};

export const testSiteGridboxConnection = async (uuidId: string): Promise<GridboxTestResponse> => {
  const response = await apiClient.post<GridboxTestResponse>(`/${uuidId}/gridbox/test`, {});
  return response.data;
};

export const fetchSiteGridboxSummary = async (
  uuidId: string,
  payload: GridboxSummaryRequest,
): Promise<GridboxSummaryResponse> => {
  const response = await apiClient.post<GridboxSummaryResponse>(`/${uuidId}/gridbox/summary`, payload);
  return response.data;
};

export const refreshSiteGridboxSummary = async (
  uuidId: string,
  payload: GridboxSummaryRequest,
): Promise<GridboxSummaryResponse> => {
  const response = await apiClient.post<GridboxSummaryResponse>(`/${uuidId}/gridbox/refresh`, payload);
  return response.data;
};

export const fetchSiteGridboxArchive = async (
  uuidId: string,
  params: { limit?: number; startDate?: string; endDate?: string } = {},
): Promise<GridboxArchiveResponse> => {
  const response = await apiClient.get<GridboxArchiveResponse>(`/${uuidId}/gridbox/archive`, {
    params,
  });
  return response.data;
};

export const fetchSiteGridboxEndexArchive = async (
  uuidId: string,
  params: { limit?: number; startDate?: string; endDate?: string; isBilling?: boolean } = {},
): Promise<GridboxEndexArchiveResponse> => {
  const response = await apiClient.get<GridboxEndexArchiveResponse>(`/${uuidId}/gridbox/endex-archive`, {
    params,
  });
  return response.data;
};

export interface GridboxCoverageDay {
  date: string;
  load_profile: { raw: number; data: number };
  endex: { raw: number; data: number };
  endex_billing: { raw: number; data: number };
}

export interface GridboxCoverageResponse {
  ok: boolean;
  siteName: string;
  year: number;
  month: number;
  days: GridboxCoverageDay[];
  fetchedAt: string;
}

export const fetchSiteGridboxCoverage = async (
  uuidId: string,
  year: number,
  month: number,
): Promise<GridboxCoverageResponse> => {
  const response = await apiClient.get<GridboxCoverageResponse>(`/${uuidId}/gridbox/coverage`, {
    params: { year, month },
  });
  return response.data;
};

export const fetchSiteProductionSeries = async (
  uuidId: string,
  params: SiteProductionSeriesRequest = {},
): Promise<SiteProductionSeriesResponse> => {
  const response = await apiClient.get<SiteProductionSeriesResponse>(`/${uuidId}/production-series`, {
    params,
  });
  return response.data;
};

export const fetchSiteEkkCurrent = async (uuidId: string): Promise<SiteEkkCurrentResponse> => {
  const response = await apiClient.get<SiteEkkCurrentResponse>(`/${uuidId}/ekk/current`);
  return response.data;
};

export const fetchSiteEkkHistory = async (
  uuidId: string,
  params: { registerMapId: number; date: string },
): Promise<SiteEkkHistoryResponse> => {
  const response = await apiClient.get<SiteEkkHistoryResponse>(`/${uuidId}/ekk/history`, { params });
  return response.data;
};
